import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import ScrollReveal from '../components/ScrollReveal';
import { favorites, communities, aboutPortraits } from '../data/mockData';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { resolveImageSrc } from '../utils/cloudinary';

const portraitLayouts = [
  { aspect: 'aspect-square', rotate: '-rotate-2' },
  { aspect: 'aspect-[4/3.3]', rotate: 'rotate-1' },
  { aspect: 'aspect-square', rotate: '-rotate-2' },
];

function portraitJustifyClass(align) {
  if (align === 'end') return 'justify-end';
  if (align === 'start') return 'justify-start';
  return 'justify-center';
}

function PortraitImage({ src, alt, width, height }) {
  return (
    <img
      src={resolveImageSrc(src, { width, height, crop: 'fill' })}
      alt={alt}
      className="h-full rounded-md shrink-0"
      loading="lazy"
    />
  );
}

function CommunityPlaceholder({ bg, name, description, icon }) {
  return (
    <div className="w-full min-h-48 rounded-md flex items-end p-4 text-xl text-inkwell-900 flex-col gap-8 text-left" style={{ background: bg }}>
      <span className="opacity-80 w-full flex items-center gap-2"><span className="text-xl inline-block transition-transform duration-300 group-hover:scale-110">{icon}</span>{name}</span>
      <p className="text-[12.5px] text-inkwell-700 leading-relaxed">{description}</p>
    </div>
  );
}

function BookCard({ item }) {
  return (
    <div className="flex flex-col gap-2.5 hover-frame rounded-md">
      <div className="relative w-full aspect-[2/3] rounded-md overflow-hidden shadow-md border border-cream-300/60 bg-cream-100">
        {item.image ? (
          <img
            src={resolveImageSrc(item.image, { width: 400, height: 600, crop: 'fill' })}
            alt={item.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: item.color }}
          >
            <span className="text-white/35 text-3xl">★</span>
          </div>
        )}
      </div>
      <div className="text-center px-1">
        <div className=" text-sm leading-tight text-inkwell-900">{item.title}</div>
        {item.author && (
          <div className="text-[10px] text-inkwell-500 mt-1">{item.author}</div>
        )}
      </div>
    </div>
  );
}

const FAVORITES_VISIBLE = 4;

function FavoritesCarousel({ categories, intervalMs = 4000 }) {
  const [categoryIndex, setCategoryIndex] = useState(0);
  const autoTimerRef = useRef(null);
  const total = categories.length;
  const canPrev = categoryIndex > 0;
  const canNext = categoryIndex < total - 1;
  const [name, items] = categories[categoryIndex];
  const visibleItems = items.slice(0, FAVORITES_VISIBLE);

  const clearAutoTimer = useCallback(() => {
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  const startAutoTimer = useCallback(() => {
    if (total <= 1) return;
    clearAutoTimer();
    autoTimerRef.current = setInterval(() => {
      setCategoryIndex((i) => (i + 1) % total);
    }, intervalMs);
  }, [total, intervalMs, clearAutoTimer]);

  const goToCategory = useCallback((index) => {
    setCategoryIndex(index);
    startAutoTimer();
  }, [startAutoTimer]);

  const goPrev = useCallback(() => {
    setCategoryIndex((i) => Math.max(0, i - 1));
    startAutoTimer();
  }, [startAutoTimer]);

  const goNext = useCallback(() => {
    setCategoryIndex((i) => Math.min(total - 1, i + 1));
    startAutoTimer();
  }, [startAutoTimer, total]);

  useEffect(() => {
    startAutoTimer();
    return () => clearAutoTimer();
  }, [startAutoTimer, clearAutoTimer]);

  return (
    <div className="mt-10">
      <h3 key={name} className=" text-2xl sm:text-3xl text-inkwell-900 text-center mb-6 category-fade">{name}</h3>
      <div className="relative flex items-center justify-center gap-2 sm:gap-4">
        <button
          type="button"
          onClick={goPrev}
          disabled={!canPrev}
          className="p-2 rounded-md bg-cream-200 border border-cream-300 disabled:opacity-30 hover:bg-cream-300 transition-colors shrink-0 btn-magnetic"
          aria-label="Previous category"
        >
          <ChevronLeft className="w-4 h-4 text-inkwell-700" />
        </button>
        <div className="overflow-hidden flex-1 max-w-3xl">
          <div key={name} className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 category-fade">
            {visibleItems.map((item, i) => (
              <BookCard
                key={`${name}-${i}-${item.title}-${item.author}`}
                item={item}
              />
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={goNext}
          disabled={!canNext}
          className="p-2 rounded-md bg-cream-200 border border-cream-300 disabled:opacity-30 hover:bg-cream-300 transition-colors shrink-0 btn-magnetic"
          aria-label="Next category"
        >
          <ChevronRight className="w-4 h-4 text-inkwell-700" />
        </button>
      </div>
      <div className="flex justify-center gap-1.5 mt-5">
        {categories.map(([categoryName], i) => (
          <button
            key={categoryName}
            type="button"
            onClick={() => goToCategory(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === categoryIndex ? 'w-5 bg-inkwell-700' : 'w-1.5 bg-cream-300 hover:bg-cream-400'
            }`}
            aria-label={`Show ${categoryName}`}
            aria-current={i === categoryIndex ? 'true' : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <Layout>
      <ScrollReveal>
        <div className="max-w-4xl">
          <h1 className=" text-4xl sm:text-5xl text-inkwell-900">Hi, I&rsquo;m Manisha!</h1>
          <p className="mt-6 text-[14px] text-inkwell-700 leading-relaxed">
          I grew up in Delhi, India, and am now exploring the design landscape in Canada as a Product Designer. Over the past 8+ years, I’ve had the opportunity to build digital products and brands, shaping experiences that are both functional and meaningful. What started as sketching in childhood gradually evolved into designing in digital tools—turning a creative instinct into a career that continues to excite and challenge me.
          </p>
          <p className="mt-4 text-[14px] text-inkwell-700">Outside of work, you'll find me:</p>
          <ul className="mt-3 space-y-2 text-[14px] text-inkwell-700">
            <li className="flex items-start gap-2"><span className="text-inkwell-400">•</span><span>Watching anime and reading manga and novels.</span></li>
            <li className="flex items-start gap-2"><span className="text-inkwell-400">•</span><span>Binge-watching period dramas</span></li>
            <li className="flex items-start gap-2"><span className="text-inkwell-400">•</span><span>Exploring trends and visual inspiration online</span></li>
          </ul>
          <p className="mt-6 text-[14px] text-inkwell-700">
            Always up for a good chat! whether it’s about design, opportunities, or just exchanging ideas.
          </p>
        </div>
      </ScrollReveal>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
        {aboutPortraits.map((portrait, i) => {
          const layout = portraitLayouts[i];
          return (
            <ScrollReveal key={portrait.src} delay={i * 80}>
              <div className={`transform ${layout.rotate} hover-lift rounded-md`} style={{ boxShadow: 'none' }}>
                <div className={`${layout.aspect} flex items-center overflow-hidden rounded-md ${portraitJustifyClass(portrait.align)}`}>
                  <PortraitImage
                    src={portrait.src}
                    alt={portrait.alt}
                    width={portrait.width}
                    height={portrait.height}
                  />
                </div>
              </div>
            </ScrollReveal>
          );
        })}
      </div>

      {/* Communities */}
      <ScrollReveal>
        <div className="mt-16">
          <p className=" text-[13px] uppercase tracking-[0.25em] text-inkwell-600 mb-6" style={{ fontWeight: '600' }}>Work Experience</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {communities.map((c, ci) => (
              <ScrollReveal key={c.name} delay={ci * 70} y={16}>
                <div className="group flex flex-col gap-3 justify-between h-full">
                  <div className="transition-transform duration-500 group-hover:-translate-y-1">
                    <CommunityPlaceholder bg={c.bg} name={c.name.split(' ')[0]} description={c.description} icon={c.icon}/>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </ScrollReveal>

      {/* Favorites */}
      <ScrollReveal>
        <div className="mt-20">
          <p className="text-center text-[13px] uppercase tracking-[0.25em] text-inkwell-700">A few of my favorite things</p>
          <FavoritesCarousel categories={Object.entries(favorites)} />
        </div>
      </ScrollReveal>

      <div className="h-10" />
    </Layout>
  );
}
